import React from "react";
import { Card, Button, Space, Spin, message } from "antd";
import { describeColor } from "./utils";

/**
 * 颜色库组件
 * - 负责展示/编辑/删除 palette
 * - 删除模式“苹果式抖动”效果
 * 提示(EN): Click the color dot to open native color picker; use Edit->Done to enable quick delete.
 */
export default function ColorLibrary({
  palette,
  setPalette,
  loading,
  loadTheme,
  MAX_COLORS,
  deleteMode,
  setDeleteMode,
  deleteSet,
  togglePickToDelete,
  removePaletteColor,
}) {
  const addPaletteColor = () =>
    setPalette((prev) =>
      prev.length >= MAX_COLORS ? (message.info("Up to 10 colors only"), prev) : [...prev, "#6b7280"]
    );

  const updatePalette = (i, color) =>
    setPalette((prev) => prev.map((c, idx) => (idx === i ? color || c : c)));

  return (
    <Card
      title={(
        <Space size={10}>
          <span className="cms-section-title text-[#1f2a44]">
            Colour Library ({palette.length ?? 0}/{MAX_COLORS})
          </span>
          <Spin spinning={loading} size="small" />
        </Space>
      )}
      className="overflow-hidden rounded-[26px] border border-[#e3e9f8] shadow-[0_18px_40px_rgba(30,52,120,0.08)]"
      headStyle={{ borderBottom: "1px solid #e8edf8", padding: "18px 22px", minHeight: "unset" }}
      bodyStyle={{ padding: "22px" }}
      bordered={false}
      extra={(
        <Space size={10}>
          <Button className="rounded-xl border-[#d9e3ff] shadow-none" onClick={loadTheme}>Reload</Button>
          {!deleteMode ? (
            <Button className="rounded-xl border-[#d9e3ff] shadow-none" onClick={() => setDeleteMode(true)}>Edit</Button>
          ) : (
            <Button danger className="rounded-xl shadow-none" onClick={() => setDeleteMode(false)}>Done</Button>
          )}
        </Space>
      )}
    >
      {/* 抖动动画样式 */}
      <style>{`
        @keyframes jiggle {
          0%   { transform: rotate(-1.5deg) translateY(0); }
          50%  { transform: rotate( 1.5deg) translateY(-1px); }
          100% { transform: rotate(-1.5deg) translateY(0); }
        }
        .palette-item.jiggle { animation: jiggle 0.22s ease-in-out infinite; transform-origin: 50% 50%; }
        .palette-delete-badge { width: 18px; height: 18px; border-radius: 50%; background: #ff4d4f; color: #fff; border: none; font-size: 12px; line-height: 18px; display: inline-block; text-align: center; cursor: pointer; }
      `}</style>

      {/* 一行最多5个，最多两行 */}
      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 24, justifyItems: "center", alignItems: "start" }}
      >
        {palette.map((hex, i) => (
          <div key={i} className={`palette-item ${deleteMode ? "jiggle" : ""}`} style={{ position: "relative", textAlign: "center", minHeight: 118, width: "100%" }}>
            {/* 圆形色块按钮（点击打开原生 color 选择器） */}
            <button
              onClick={() => {
                if (deleteMode) { togglePickToDelete(i); return; }
                const el = document.getElementById(`color-input-${i}`);
                if (el) el.click();
              }}
              style={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                border: "1px solid rgba(148,163,184,0.28)",
                background: hex,
                boxShadow: "0 12px 22px rgba(15,23,42,0.12)",
                cursor: "pointer",
                marginBottom: 10,
                transition: "transform 220ms ease, box-shadow 220ms ease",
                ...(deleteMode && deleteSet.has(i) ? { boxShadow: "0 0 0 2px #ff4d4f, 0 12px 22px rgba(15,23,42,0.12)" } : {}),
              }}
              title={hex}
            />
            {/* 隐藏取色器 */}
            <input id={`color-input-${i}`} type="color" value={hex} onChange={(e) => updatePalette(i, e.target.value)} style={{ position: "absolute", inset: 0, opacity: 0, pointerEvents: "none" }} aria-hidden />
            {/* 下方 HEX + 删除（仅删除模式） */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>{describeColor(hex)}</span>
              <div style={{ fontSize: 12, color: "#94a3b8", display: "flex", alignItems: "center", gap: 6 }}>
                <span>{hex.toUpperCase()}</span>
                {deleteMode && (
                  <button className="palette-delete-badge" onClick={() => removePaletteColor(i)} title="Delete this colour">－</button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* “＋” 按钮 */}
        {palette.length < MAX_COLORS && (
          <div style={{ textAlign: "center", minHeight: 118, opacity: deleteMode ? 0.5 : 1, width: "100%" }}>
            <button
              onClick={!deleteMode ? addPaletteColor : undefined}
              disabled={deleteMode}
              style={{ width: 58, height: 58, borderRadius: "50%", border: "1px dashed rgba(148,163,184,0.55)", background: "#fbfcff", color: "rgba(71,85,105,0.72)", fontSize: 28, lineHeight: "58px", cursor: deleteMode ? "not-allowed" : "pointer", marginBottom: 10 }}
              title={deleteMode ? "Exit edit mode to add" : "Add Colour"}
            >
              +
            </button>
            <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>Add</div>
          </div>
        )}
      </div>
    </Card>
  );
}
